/**
 * <e-search>: sugestões da busca do cabeçalho (snippet e-search-dialog).
 *
 * O formulário é nativo (GET /search) e funciona sem este arquivo. Com ele, a
 * partir de 2 caracteres e 300 ms depois da última tecla (exemplo da Shopify),
 * pede /search/suggest com section_id=e-search-results (Section Rendering API)
 * e mostra o HTML dessa seção. Cancela o pedido anterior e guarda as respostas
 * desta visita. O campo é um combobox ARIA 1.2: setas percorrem as opções por
 * aria-activedescendant, Enter abre a opção ativa.
 *
 * Estados em data-state (o CSS mostra e esconde as partes): idle, loading,
 * results, empty e error.
 */

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 300;
const CACHE_LIMIT = 30;
const RESOURCE_TYPES = 'product,collection,page,article';
const RESOURCE_LIMIT = '6';

class EternitaSearch extends HTMLElement {
  #input = null;
  #results = null;
  #status = null;
  #controller = null;
  #timer = 0;
  #cache = new Map();
  #activeIndex = -1;
  #shownTerms = '';

  connectedCallback() {
    this.#input = this.querySelector('input[type="search"]');
    this.#results = this.querySelector('[data-e-search-results]');
    this.#status = this.querySelector('[data-e-search-status]');
    if (!this.#input || !this.#results) return;
    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('keydown', this.#onKeydown);
    this.#input.addEventListener('focus', this.#onFocus);
    this.#input.form?.addEventListener('submit', this.#onSubmit);
    this.closest('dialog')?.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    this.#cancel();
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    this.#input?.removeEventListener('focus', this.#onFocus);
    this.#input?.form?.removeEventListener('submit', this.#onSubmit);
    this.closest('dialog')?.removeEventListener('close', this.#onClose);
  }

  get #terms() {
    return this.#input.value.trim();
  }

  get #options() {
    return [...this.#results.querySelectorAll('[role="option"]')];
  }

  /* ---------- Eventos ---------- */
  #onInput = () => {
    /* Editar o texto limpa a opção ativa (APG combobox): o Enter volta a buscar o termo novo. */
    this.#setActive(-1);
    this.#cancel();
    const terms = this.#terms;
    if (terms.length < MIN_LENGTH) {
      this.#reset();
      return;
    }
    const cached = this.#cache.get(terms);
    if (cached) {
      this.#render(terms, cached);
      return;
    }
    this.#timer = window.setTimeout(() => this.#fetch(terms), DEBOUNCE_MS);
  };

  /* Reabrir a busca com um termo que ainda não tem sugestões na tela (valor
     vindo de /search ou digitado logo antes de fechar) busca de novo. Pedido
     em andamento segue; fechar no meio dele volta a idle (#onClose). */
  #onFocus = () => {
    const terms = this.#terms;
    if (this.dataset.state === 'loading' || terms.length < MIN_LENGTH || terms === this.#shownTerms) return;
    this.#onInput();
  };

  #onSubmit = (event) => {
    if (this.#terms) return;
    event.preventDefault();
    this.#input.focus();
  };

  #onClose = () => {
    this.#cancel();
    if (this.dataset.state === 'loading') this.#reset();
    this.#setActive(-1);
  };

  #onKeydown = (event) => {
    const options = this.#options;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!options.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.#activeIndex + step;
      /* Descer da última volta à primeira; subir da primeira volta ao campo. */
      if (next >= options.length) this.#setActive(0);
      else if (next < -1) this.#setActive(options.length - 1);
      else this.#setActive(next);
      return;
    }
    if (event.key === 'Enter' && this.#activeIndex >= 0) {
      event.preventDefault();
      options[this.#activeIndex]?.click();
    }
  };

  /* ---------- Busca ---------- */
  async #fetch(terms) {
    this.#controller = new AbortController();
    this.dataset.state = 'loading';
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', RESOURCE_TYPES);
    url.searchParams.set('resources[limit]', RESOURCE_LIMIT);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.dataset.section);

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('.e-search-results');
      if (!fragment) throw new Error('Resposta sem .e-search-results');
      this.#remember(terms, fragment);
      if (terms === this.#terms) this.#render(terms, fragment);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (terms === this.#terms) this.#fail();
    }
  }

  #cancel() {
    window.clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#controller = null;
  }

  #remember(terms, fragment) {
    this.#cache.delete(terms);
    this.#cache.set(terms, fragment);
    if (this.#cache.size > CACHE_LIMIT) this.#cache.delete(this.#cache.keys().next().value);
  }

  /* ---------- Estados ---------- */
  #render(terms, fragment) {
    const count = Number(fragment.dataset.eSearchCount) || 0;
    this.#results.replaceChildren(document.importNode(fragment, true));
    this.#shownTerms = terms;
    this.#activeIndex = -1;
    this.#input.removeAttribute('aria-activedescendant');
    this.#input.setAttribute('aria-expanded', String(count > 0));
    this.toggleAttribute('data-has-results', count > 0);
    this.dataset.state = count > 0 ? 'results' : 'empty';
    const noun = count === 1 ? 'sugestão' : 'sugestões';
    this.#announce(
      count > 0
        ? `${count} ${noun} para ${terms}. Use as setas para navegar.`
        : `Nenhum resultado para ${terms}.`,
    );
  }

  #reset() {
    this.#clear('idle', '');
  }

  #fail() {
    this.#clear('error', 'Não foi possível carregar as sugestões.');
  }

  #clear(state, message) {
    this.#results.replaceChildren();
    this.#shownTerms = '';
    this.#activeIndex = -1;
    this.#input.removeAttribute('aria-activedescendant');
    this.#input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-has-results');
    this.dataset.state = state;
    this.#announce(message);
  }

  #setActive(index) {
    const options = this.#options;
    options[this.#activeIndex]?.setAttribute('aria-selected', 'false');
    this.#activeIndex = index;
    const option = options[index];
    if (!option) {
      this.#input.removeAttribute('aria-activedescendant');
      return;
    }
    option.setAttribute('aria-selected', 'true');
    this.#input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }
}

if (!customElements.get('e-search')) customElements.define('e-search', EternitaSearch);
